import { equals } from 'ramda';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { ColumnsProps } from './useFilledDisclosuresListColumns';

import {
    ConversationStatus,
    decryptMessage,
    setEncryptionSecret,
    useConversationsStatusContext,
} from '@speeki/firebase-chat';
import { DateFormat } from '@speeki/frontend-utils';
import { notify, useTablePagination } from '@speeki/global-ui-components';
import { IconManager } from '@speeki/icons';
import { usePrevious } from '@speeki/react-hooks';

import {
    DisclosureDetailsDocument,
    FilledDisclosuresListDocument,
} from '@graphql/generated/graphql';

import { getPath } from '@services/RouteService/paths';

import { useLazyQuery, useQuery } from '@apollo/client';
import { FilledDisclosuresCell } from '@domains/disclosures/molecules/FilledDisclosuresCell/FilledDisclosuresCell';
import { useOpenInfoModal } from '@domains/disclosures/useOpenInfoModal';

export const useFilledDisclosuresListPage = () => {
    const { disclosureUuid } = useParams<{ disclosureUuid: string }>();

    const { palette } = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { openInfoModal } = useOpenInfoModal();

    //TODO: select conversation by id instead whole collection to prevent unnecessary re-rendings
    const conversationsStatus = useConversationsStatusContext();

    const [
        getFilledDisclosuresList,
        { data: disclosureListData, loading: loadingList, refetch },
    ] = useLazyQuery(FilledDisclosuresListDocument, {
        fetchPolicy: 'cache-and-network',
    });

    const { data: disclosureData, loading: loadingDetails } = useQuery(
        DisclosureDetailsDocument,
        {
            fetchPolicy: 'network-only',
            onError: ({ message }) => {
                notify(message).ERROR();
            },
            variables: {
                disclosureUuid: disclosureUuid ?? '',
                withJson: false,
            },
        },
    );

    const pagination = useTablePagination({
        onPageChange: ({ limit, offset }) => {
            void getFilledDisclosuresList({
                variables: {
                    input: {
                        disclosureUuid: disclosureUuid ?? '',
                        paging: {
                            limit,
                            offset,
                        },
                    },
                },
            });
        },
        testId: '4xTRmGe',
        totalCount: disclosureListData?.list.totalCount ?? 0,
    });

    const renderMessage = (id: string, chatRoomSecret?: string | null) => {
        const decryptedMessage =
            chatRoomSecret &&
            decryptMessage(id, conversationsStatus?.[id]?.messageContent);
        const date = conversationsStatus?.[id]?.sentAt?.toDate() || '';
        const isAttachment = date && !decryptedMessage;

        if (conversationsStatus?.[id]) {
            if (isAttachment) return t('ATTACHMENT');
            if (!!decryptedMessage) return decryptedMessage;
            return t('NO_MESSAGES_YET');
        }

        if (!chatRoomSecret) return t('NO_MESSAGES_YET');
        return '';
    };

    const renderMessageStatus = (status: ConversationStatus['status']) => {
        switch (status) {
            case 'seen': {
                return <IconManager icon={'SvgEnvelopeOpen24'} size={24} />;
            }
            case 'new': {
                return (
                    <IconManager icon={'SvgEnvelopeNotification24'} size={24} />
                );
            }
            default: {
                return '-';
            }
        }
    };

    const isLoading = loadingList || loadingDetails;
    const disclosureDetails = disclosureData?.details;
    const disclosureList: ColumnsProps[] =
        disclosureListData?.list.nodes.map(
            ({ approvalStatus, chatRoomSecret, submissionDate, uuid }) => {
                chatRoomSecret &&
                    setEncryptionSecret(uuid, chatRoomSecret || '');

                const status = conversationsStatus?.[uuid]?.status;
                const isNew = status === 'new';
                const date =
                    conversationsStatus?.[uuid]?.sentAt?.toDate() || '';
                const message = renderMessage(uuid, chatRoomSecret);

                const approvalStatuses = {
                    APPROVED: 'APPROVED',
                    DISAPPROVED: 'DECLINED',
                    NONE: '-',
                };

                return {
                    id: uuid,
                    approvalStatusCell: (
                        <FilledDisclosuresCell
                            customColor={
                                approvalStatus === 'APPROVED'
                                    ? palette.semantic.success
                                    : approvalStatus === 'DISAPPROVED'
                                      ? palette.semantic.error
                                      : undefined
                            }
                            isNew={isNew}
                        >
                            {approvalStatus
                                ? approvalStatuses[approvalStatus]
                                : '-'}
                        </FilledDisclosuresCell>
                    ),
                    lastMessageDate: date,
                    lastMessageDateCell: (
                        <FilledDisclosuresCell isNew={isNew}>
                            {date ? DateFormat.standard(date) : null}
                        </FilledDisclosuresCell>
                    ),
                    message: message,
                    messageCell: (
                        <FilledDisclosuresCell isNew={isNew}>
                            {message}
                        </FilledDisclosuresCell>
                    ),
                    status,
                    statusCell: (
                        <FilledDisclosuresCell status>
                            {renderMessageStatus(status)}
                        </FilledDisclosuresCell>
                    ),
                    submissionCell: (
                        <FilledDisclosuresCell isHighlighted isNew={isNew}>
                            {DateFormat.fullDate(submissionDate)}
                        </FilledDisclosuresCell>
                    ),
                    submissionDate,
                };
            },
        ) ?? [];

    const openPersonalInfoModal = () => openInfoModal(disclosureUuid ?? '');

    const openSubmission = (submissionUuid: string) =>
        navigate(
            getPath({
                params: { disclosureUuid, submissionUuid },
                path: '/disclosure/list/:disclosureUuid/:submissionUuid',
            }),
        );

    const prevConversationsStatus = usePrevious(conversationsStatus);
    const equalStatus = equals(conversationsStatus, prevConversationsStatus);
    const prevEqualsStatus = usePrevious(equalStatus);
    const triggerRefetch = equals(equalStatus, prevEqualsStatus);

    useEffect(() => {
        /**
         * FIXME: The following solution is dirty workaround, so feel free to improve. We want to refetch only once when triggerRefetch change.
         */
        triggerRefetch && void refetch();
    }, [triggerRefetch]);

    return {
        disclosureDetails,
        disclosureList,
        isLoading,
        openPersonalInfoModal,
        openSubmission,
        pagination,
        t,
    };
};
